/**
 * ⛏️ **The cracks that spread across a block as you break it.**
 *
 * 🎯 **haugur, 2026-09-01:** *"in the game now if you hold the mouse button a
 * white box appears and gets smaller and smaller until the block breaks… look at
 * research how minecraft does this, cracks appear on block and stay until block
 * is broken, i like that."*
 *
 * ## 🔴 Drawn triangles, not a texture — and the reason is an outage
 *
 * Minecraft draws its destroy stages as **ten textures** laid over the block
 * face. The obvious way to copy that is a textured overlay, and **that is
 * exactly what turned the whole game black on 2026-09-01**: putting a texture on
 * a material is a new shader permutation, `06_Traps.md` says a material is
 * untested until something is drawn with it, and **there is no WebGPU adapter on
 * the workstation this was written on** — measured, not assumed: `navigator.gpu`
 * is not exposed by headless Chromium or Edge here at all.
 *
 * ✅ **So the cracks are ordinary triangles wearing the `MeshBasicMaterial`
 * setup the old white break-box already proved works** — transparent, no depth
 * test, no map. **Nothing new has to compile.** ⚠️ It is a genuine trade: real
 * cracks in a texture would look better than drawn ones. **A worse picture that
 * renders beats a better one that does not**, and this can be revisited the day
 * there is a machine that can check a shader.
 *
 * ## ⚠️ They were LINES first, and the width is why they are not
 *
 * 🎯 **haugur, 2026-09-02:** *"crack lines should be thicker, maby 2 or 3 times
 * thicker."* 🔴 **`LineBasicMaterial.linewidth` is ignored** — checked in three
 * 0.180 rather than recalled: only the *fat line* node material honours a width,
 * and adopting that would have meant **a new material**, the very thing that had
 * just taken the game down twice. 👉 **So the width lives in the geometry**, and
 * every fracture is a flat ribbon of two triangles lying in the face.
 *
 * ## How it grows
 *
 * Segments are ordered **by ring, not by branch** — the first few reveal a short
 * fracture at the centre of every face at once, and later ones push those
 * fractures outward. Revealing one whole branch at a time would read as a line
 * being drawn rather than as a block failing.
 *
 * ⚠️ **All six faces are cracked.** You may be looking at a block from any side,
 * and the damage now survives you walking away (**D104**) — so somebody will
 * come back to a half-broken block from the other side.
 */

/**
 * A small deterministic pseudo-random source.
 *
 * 🔴 **Deterministic on purpose.** The same block must crack the same way every
 * frame, or the pattern boils. It also makes the geometry testable, which
 * `Math.random` would not be.
 */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * ⛏️ **How wide a fracture is, in metres on a one-metre block.**
 *
 * 🎯 **haugur, 2026-09-02, having seen the first version:** *"crack lines should
 * be thicker, maby 2 or 3 times thicker."* **This is that number** — it is the
 * only one to change if they are still wrong.
 *
 * ⚠️ **World units, not pixels**, so a crack thins with distance the way
 * anything else in the world does. A pixel width would keep a block fifty metres
 * away as boldly cracked as one under your nose.
 */
export const CRACK_WIDTH = 0.02;

/** How many fractures start from the middle of each face. */
export const BRANCHES = 4;
/** How many steps each fracture walks outward. */
export const RINGS = 5;
/** Six faces × four fractures × five steps. */
export const TOTAL_SEGMENTS = 6 * BRANCHES * RINGS;

/**
 * 🔴 **Six — two triangles.** It was TWO while the cracks were lines.
 *
 * ⚠️ **Named rather than written as a 6 at the call sites.** `setDrawRange`
 * takes vertices, and a factor that appears in two files is a factor that gets
 * changed in one of them.
 */
export const VERTICES_PER_SEGMENT = 6;

/**
 * The six faces, as an origin and two edge vectors on the unit cube.
 *
 * ⚠️ **Pushed a hair OUTSIDE the block** — `OUT` below — so the lines are not
 * co-planar with the block's own surface. Co-planar lines z-fight, and the
 * symptom is a crack that flickers as you move, which reads as a rendering fault
 * rather than as damage.
 */
const OUT = 0.503;
const FACES: readonly {
  readonly at: readonly [number, number, number];
  readonly u: readonly [number, number, number];
  readonly v: readonly [number, number, number];
}[] = [
  { at: [0, 0, OUT], u: [1, 0, 0], v: [0, 1, 0] },
  { at: [0, 0, -OUT], u: [1, 0, 0], v: [0, 1, 0] },
  { at: [OUT, 0, 0], u: [0, 0, 1], v: [0, 1, 0] },
  { at: [-OUT, 0, 0], u: [0, 0, 1], v: [0, 1, 0] },
  { at: [0, OUT, 0], u: [1, 0, 0], v: [0, 0, 1] },
  { at: [0, -OUT, 0], u: [1, 0, 0], v: [0, 0, 1] },
];

/**
 * ⛏️ **Build the crack lines for a unit cube**, as a flat `x,y,z` array of
 * segment endpoints — two points per segment, ready for `BufferGeometry`.
 *
 * **Ordered by ring**: the first `6 × BRANCHES` segments are the innermost step
 * of every fracture on every face, then the next step outward, and so on. Draw
 * the first N and you have a block that is N/TOTAL broken.
 */
export function crackGeometry(seed = 1): Float32Array {
  const random = seeded(seed);
  /* rings[r] collects every segment belonging to step r, across all faces. */
  const rings: number[][] = Array.from({ length: RINGS }, () => []);

  FACES.forEach((face, faceIndex) => {
    for (let b = 0; b < BRANCHES; b++) {
      /* Fractures leave the centre in roughly even directions, jittered so the
         six faces do not look stamped from one die. */
      let angle = (b / BRANCHES) * Math.PI * 2 + random() * 0.9 + faceIndex;
      let u = 0;
      let v = 0;
      for (let r = 0; r < RINGS; r++) {
        const from: [number, number] = [u, v];
        /* Each step is shorter than the last, so a fracture slows as it
           reaches the edge instead of shooting off the block. */
        const len = 0.085 + 0.02 * (RINGS - r) + random() * 0.03;
        angle += (random() - 0.5) * 1.1;
        u += Math.cos(angle) * len;
        v += Math.sin(angle) * len;
        /* Stay on the face. A crack that leaves the block is a stray line in
           mid-air, and at a distance that is all anybody would see. */
        u = Math.max(-0.47, Math.min(0.47, u));
        v = Math.max(-0.47, Math.min(0.47, v));

        /* ⚠️ `.at(r)`, not `rings[r]` — indexing an array with a variable is the
           object-injection sink the linter refuses, and it refuses it project
           wide rather than case by case. */
        const ring = rings.at(r);
        if (ring === undefined) continue;

        /**
         * 🔴 **The fracture is widened HERE, into triangles, rather than by
         * asking the material for a thicker line.**
         *
         * 🎯 **haugur, 2026-09-02:** *"crack lines should be thicker, maby 2 or
         * 3 times thicker."*
         *
         * ⚠️ **`LineBasicMaterial.linewidth` is ignored** — checked in three
         * 0.180 rather than recalled: only the *fat line* node material honours
         * a width, and adopting that would mean **a new material**, which is
         * precisely what turned the game black twice on 2026-09-01. ✅ **Width
         * built into the geometry needs no new shader at all.**
         *
         * Each segment becomes a flat ribbon lying in the face: the segment
         * direction, its perpendicular in the same plane, and two triangles.
         */
        const du = u - from[0];
        const dv = v - from[1];
        const length = Math.hypot(du, dv) || 1;
        /* Perpendicular in the face's own 2D space, scaled to half the width. */
        const nu = (-dv / length) * (CRACK_WIDTH / 2);
        const nv = (du / length) * (CRACK_WIDTH / 2);

        const corner = (pu: number, pv: number, su: number, sv: number): number[] => [
          face.at[0] + face.u[0] * (pu + su) + face.v[0] * (pv + sv),
          face.at[1] + face.u[1] * (pu + su) + face.v[1] * (pv + sv),
          face.at[2] + face.u[2] * (pu + su) + face.v[2] * (pv + sv),
        ];
        const a = corner(from[0], from[1], nu, nv);
        const b2 = corner(from[0], from[1], -nu, -nv);
        const c = corner(u, v, nu, nv);
        const d = corner(u, v, -nu, -nv);
        /* Two triangles, wound the same way. ⚠️ The material is double-sided,
           so a crack on a face you are looking at from behind still draws — the
           damage belongs to the block, not to one side of it. */
        ring.push(...a, ...b2, ...c, ...c, ...b2, ...d);
      }
    }
  });

  return new Float32Array(rings.flat());
}

/**
 * ⛏️ **How many crack VERTICES to draw for this much damage**, 0…1.
 *
 * 🔴 **Never zero once anything has been done to the block.** A first blow that
 * shows nothing reads as the game not registering the click — `Minecraft/11` §4
 * calls the progressive damage overlay part of the tutorial itself, *"the
 * difference between a verb that feels real and one that feels like a network
 * request."*
 *
 * ⚠️ **Returns VERTICES rather than segments**, because that is what
 * `setDrawRange` wants and converting at the call site is one more place to get
 * the factor wrong — and it CHANGED from two to six when the cracks stopped
 * being lines and became ribbons.
 */
export function crackVertexCount(damage: number): number {
  if (damage <= 0) return 0;
  const clamped = Math.min(1, damage);
  const segments = Math.max(BRANCHES, Math.ceil(clamped * TOTAL_SEGMENTS));
  return Math.min(TOTAL_SEGMENTS, segments) * VERTICES_PER_SEGMENT;
}
